import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  image = "https://res.cloudinary.com/dsez5ypdi/image/upload/w_1200,h_630,c_fill,g_auto,q_auto,f_auto/gallery/gallery_2_colorful_candles.jpg",
  url,
  type = "website",
  product = null 
}) => {
  const siteName = "Adina's Crafts";
  const baseUrl = "https://www.adinascrafts.co.uk";
  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - Handmade Gifts UK`;
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  
  // Enhanced UK handmade keywords for SEO
  const defaultKeywords = "handmade gifts UK, handmade home decor UK, personalised gifts UK, handmade resin crafts UK, handmade wedding gifts UK, handmade birthday gifts UK, handmade Christmas gifts UK, handmade Easter gifts UK, unique handmade presents UK, artisan handmade products UK, eco-friendly handmade gifts UK, handmade craft shop UK, handmade gift ideas UK, custom handmade gifts UK, handmade seasonal gifts UK, handmade accessories UK, soya wax candles UK, scented candles UK, elephant candle UK, teddy bear candle UK, flower candles UK, angel wing candles UK, resin clock UK, resin art UK, handmade candles Aylesbury, handmade gifts Milton Keynes, artisan crafts Buckinghamshire, handmade presents Oxford, craft shop High Wycombe, handmade gifts Luton, artisan candles Hemel Hempstead, unique gifts Tring, handmade Chesham, crafts Amersham, gifts Leighton Buzzard";
  
  const allKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;
  
  // UK Local Business Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${baseUrl}/#localbusiness`,
    "name": "Adina's Crafts",
    "description": "UK artisan craft shop specialising in handmade soya wax candles, resin art, and unique personalised gifts. Serving Aylesbury, Milton Keynes, Oxford, and all UK.",
    "url": baseUrl,
    "logo": "https://res.cloudinary.com/dsez5ypdi/image/upload/w_800,h_800,c_fill/gallery/gallery_6_branded_candle.jpg",
    "image": image,
    "telephone": "+447818983777",
    "email": "contact@adinascrafts.co.uk",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Aylesbury",
      "addressRegion": "Buckinghamshire",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 51.8168,
      "longitude": -0.8124
    },
    "areaServed": ["Aylesbury", "Milton Keynes", "Luton", "Oxford", "High Wycombe", "Hemel Hempstead", "Tring", "Chesham", "Amersham", "Leighton Buzzard", "Dunstable", "Princes Risborough", "Thame", "Bicester", "Wendover", "United Kingdom"],
    "priceRange": "££",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "sameAs": [
      "https://www.facebook.com/adinascrafts",
      "https://www.instagram.com/adinascrafts"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Handmade Gifts & Artisan Crafts UK",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Handmade Scented Candles",
          "description": "Hand-poured soya wax scented candles - elephant, teddy bear, flower, angel wing designs"
        },
        {
          "@type": "OfferCatalog",
          "name": "Resin Art UK",
          "description": "Handmade resin clocks and decorative pieces"
        },
        {
          "@type": "OfferCatalog",
          "name": "Personalised Gift Sets",
          "description": "Curated handmade gift boxes for birthdays, weddings, Christmas"
        }
      ]
    }
  };

  // Website Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Adina's Crafts - Handmade Gifts UK",
    "url": baseUrl,
    "description": "UK online shop for handmade artisan candles, resin crafts, and unique personalised gifts",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/shop?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    },
    "inLanguage": "en-GB"
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": title || "Shop",
        "item": fullUrl
      }
    ]
  };

  // Product Schema for product pages - Enhanced for Google Shopping
  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description || `Handmade ${product.name} from Adina's Crafts UK`,
    "image": image,
    "sku": product.id,
    "mpn": product.id,
    "brand": {
      "@type": "Brand",
      "name": "Adina's Crafts"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Adina's Crafts",
      "url": baseUrl
    },
    "category": "Home & Garden > Decor > Candles",
    "material": "Soya Wax",
    "countryOfOrigin": "GB",
    "offers": {
      "@type": "Offer",
      "url": fullUrl,
      "priceCurrency": "GBP",
      "price": product.price,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "availability": product.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Adina's Crafts"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "GBP"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "GB"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 2,
            "maxValue": 5,
            "unitCode": "DAY"
          }
        }
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    }
  } : null;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content="Adina's Crafts" />
      <meta name="geo.region" content="GB" />
      <meta name="geo.placename" content="United Kingdom" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={product ? "product" : type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_GB" />
      
      {/* Product specific OG tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content="GBP" />
          <meta property="product:availability" content={product.inStock !== false ? "in stock" : "out of stock"} />
          <meta property="product:brand" content="Adina's Crafts" />
          <meta property="product:condition" content="new" />
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@adinascrafts" />
      
      {/* Local Business Schema */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      
      {/* Website Schema */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      
      {/* Product Schema for product pages */}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
